import React, { useState, useRef } from "react";
import { FiEdit, FiTrash2, FiEye, FiSearch } from "react-icons/fi";
import { usePostsService } from "../../service/posts/usePostsService";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CATEGORIES } from "../../utils/constants";
import EditPostModal from "../edit-post-modal";
import DeletePostModal from "../delete-post-modal";
import { useAuth } from "../../context/AuthContext";

const DEFAULT_LIMIT = 15;

const PostsList = () => {
  const { user } = useAuth();
  const { getPosts, deletePost } = usePostsService();
  const [selectedPost, setSelectedPost] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [postToDelete, setPostToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(DEFAULT_LIMIT);
  const searchInputRef = useRef(null);

  const queryClient = useQueryClient();
  console.log(user);
  const { data: postsData, isLoading } = useQuery({
    queryKey: [
      "posts",
      { page: currentPage, limit, searchTerm, categoryFilter },
    ],
    queryFn: () => getPosts(currentPage, limit, searchTerm, categoryFilter),
    keepPreviousData: true,
  });

  const posts = postsData?.data || [];
  const totalCount = postsData?.totalCount || 0;
  const totalPages = Math.ceil(totalCount / limit);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const confirmDelete = (post) => {
    setPostToDelete(post);
  };

  const handleConfirmDelete = async () => {
    try {
      await deletePost(postToDelete.id);
      queryClient.setQueryData(
        ["posts", { page: currentPage, limit, searchTerm, categoryFilter }],
        (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            data: oldData.data.filter((p) => p.id !== postToDelete.id),
            totalCount: oldData.totalCount - 1,
          };
        }
      );
      setPostToDelete(null);
    } catch (error) {
      console.error("Error deleting post:", error);
    }
  };

  const handleEdit = (post) => {
    setSelectedPost(post);
    setIsEditModalOpen(true);
  };

  const handleClearSearch = () => {
    if (searchInputRef.current) {
      searchInputRef.current.value = "";
    }
    setSearchTerm("");
    setCurrentPage(1);
  };

  const canUserEditOrDeletePost = (post) => {
    return (
      user?.app_metadata?.role === "authenticated" ||
      user?.id === post.author_user_id
    );
  };

  return (
    <div className="p-6">
      <div className="bg-white rounded-lg shadow-lg">
        <div className="p-6 border-b">
          <h2 className="text-2xl font-bold mb-4">Menaxhimi i postimeve</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Kërko postimet..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              {searchTerm && (
                <button
                  onClick={handleClearSearch}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 text-sm"
                  title="Clear search"
                >
                  ✕
                </button>
              )}
            </div>
            <select
              value={categoryFilter}
              onChange={(e) => {
                setCategoryFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Të gjitha kategoritë</option>
              {CATEGORIES.map((cat) => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </select>
            <div className="flex items-center text-gray-600">
              {posts.length} postime të gjetura
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">Asnjë postim i gjetur</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Imazhi
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Titulli
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Autori
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Kategoritë
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Data
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Veçori
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Veprimet
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200 sm:overflow-x-scroll">
                {posts.map((post) => (
                  <tr key={post.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      {post.image_url ? (
                        <img
                          src={post.image_url}
                          alt={post.title}
                          className="h-12 w-16 object-cover rounded"
                        />
                      ) : (
                        <div className="h-12 w-16 bg-gray-200 rounded flex items-center justify-center">
                          <span className="text-gray-400 text-xs">
                            Ska imazh
                          </span>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">
                        {post.title}
                      </div>
                      <div className="text-sm text-gray-500">
                        {post.excerpt?.substring(0, 60)}...
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {post.author_name}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {post.categories?.map((cat) => {
                          const category = CATEGORIES.find(
                            (c) => c.value === cat
                          );
                          return (
                            <span
                              key={cat}
                              className="px-2 py-1 text-xs rounded-full text-white"
                              style={{
                                backgroundColor: category?.color || "#6b7280",
                              }}
                            >
                              {category?.label || cat}
                            </span>
                          );
                        })}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {new Date(post.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <div className="flex gap-1">
                        {post.is_featured && (
                          <span className="text-yellow-500" title="Featured">
                            ★
                          </span>
                        )}
                        {post.is_trending && (
                          <span className="text-red-500" title="Trending">
                            🔥
                          </span>
                        )}
                        {post.is_in_banner && (
                          <span className="text-blue-500" title="Banner">
                            🏠
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm font-medium">
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            window.open(
                              `https://balotazh.com/news/${post.id}`,
                              "_blank"
                            );
                          }}
                          className="text-gray-600 hover:text-gray-900"
                          title="Preview"
                        >
                          <FiEye />
                        </button>
                        {canUserEditOrDeletePost(post) && (
                          <button
                            onClick={() => handleEdit(post)}
                            className="text-blue-600 hover:text-blue-900"
                            title="Edit"
                          >
                            <FiEdit />
                          </button>
                        )}
                        {canUserEditOrDeletePost(post) && (
                          <button
                            onClick={() => confirmDelete(post)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete"
                          >
                            <FiTrash2 />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="mt-6 overflow-x-auto">
          <div className="flex justify-center md:justify-center gap-1 min-w-max w-full px-2 my-4">
            <button
              onClick={() => handlePageChange(Math.max(currentPage - 1, 1))}
              disabled={currentPage === 1}
              className={`min-w-[28px] px-2 py-1 text-xs font-semibold rounded-full shadow transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 ${
                currentPage === 1
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed opacity-60"
                  : "bg-white text-gray-700 hover:bg-blue-50 hover:shadow-lg active:scale-95"
              }`}
            >
              Mbrapa
            </button>

            {(() => {
              const pages = [];
              if (totalPages <= 6) {
                for (let i = 1; i <= totalPages; i++) pages.push(i);
              } else {
                pages.push(1);
                if (currentPage > 3) pages.push("ellipsis-start");
                const start = Math.max(2, currentPage - 1);
                const end = Math.min(totalPages - 1, currentPage + 1);
                for (let i = start; i <= end; i++) pages.push(i);
                if (currentPage < totalPages - 2) pages.push("ellipsis-end");
                pages.push(totalPages);
              }
              return pages.map((item, i) =>
                typeof item === "string" ? (
                  <span
                    key={`ellipsis-${i}`}
                    className="px-1 py-1 text-gray-400 font-bold select-none text-xs"
                  >
                    ...
                  </span>
                ) : (
                  <button
                    key={item}
                    onClick={() => handlePageChange(item)}
                    className={`min-w-[28px] px-2 py-1 text-xs font-semibold rounded-full shadow transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 ${
                      currentPage === item
                        ? "bg-blue-600 text-white scale-105 shadow-lg border-2 border-blue-700 z-10"
                        : "bg-white text-gray-700 hover:bg-blue-50 hover:shadow-lg active:scale-95"
                    }`}
                    aria-current={currentPage === item ? "page" : undefined}
                  >
                    {item}
                  </button>
                )
              );
            })()}

            <button
              onClick={() =>
                handlePageChange(Math.min(currentPage + 1, totalPages))
              }
              disabled={currentPage === totalPages}
              className={`min-w-[28px] px-2 py-1 text-xs font-semibold rounded-full shadow transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 ${
                currentPage === totalPages
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed opacity-60"
                  : "bg-white text-gray-700 hover:bg-blue-50 hover:shadow-lg active:scale-95"
              }`}
            >
              Tjetra
            </button>
          </div>
        </div>
      </div>

      <EditPostModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedPost(null);
        }}
        post={selectedPost}
        queryParams={{ page: currentPage, limit, searchTerm, categoryFilter }}
      />

      <DeletePostModal
        post={postToDelete}
        isOpen={Boolean(postToDelete)}
        onClose={() => setPostToDelete(null)}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
};

export default PostsList;
